/*
  Table view of the Tasks page (screen 12-tasks, idea 6). Built on the shared DataTable: sortable columns
  (sort kept in the URL), sticky header, Columns menu, check boxes with the task bulk bar (idea 9), groups
  that follow "Group by", and the old TaskTable's Duration / Progress column for multi-day work plus the
  "N tasks · X overdue · Y due today" footer — now in the app's light colours.
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): no check boxes, no bulk bar and no
  row ⋯ menu (Done / Reopen / Delete) — a row click still opens the task, read-only.
*/
import { useMemo } from 'react';
import { CircleCheck, PanelRightOpen, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber } from '@/lib/format';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ProgressBar } from '@/components/ui/progress-bar';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { DueBadge, OriginChip, PersonAvatar, PriorityFlag, StatusIcon } from './TaskCard';
import TaskBulkBar from './TaskBulkBar';
import {
  DUE_GROUPS,
  dueGroupOf,
  dueInfo,
  durationInfo,
  isCancelled,
  isClosed,
  isDone,
  isoDate,
  NO_SECTION,
  NONE_VALUE,
  normalizeStatus,
  priorityMeta,
  sectionOf,
  taskAssignees,
  taskProjects,
} from './taskModel';

const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };
const DUE_ORDER = DUE_GROUPS.map((group) => group.key);

/** Columns of the tasks table (exported so the page can build the Columns menu with the same list). */
export function useTaskColumns({ projectNameById, onOpen, onDone, onReopen, onDelete, readOnly = false }) {
  return useMemo(() => [
    {
      key: 'name',
      header: 'Task',
      sortable: true,
      hideable: false,
      minWidth: 210,
      sortValue: (task) => String(task.name || '').toLowerCase(),
      render: (task) => {
        const done = isClosed(task);
        return (
          <div className="flex min-w-0 items-start gap-2 py-1">
            <StatusIcon status={task.status} className="mt-[2px]" />
            <div className="min-w-0">
              <div dir="auto" className={cn('line-clamp-2 text-start text-[13.5px] font-semibold text-ink', done && 'text-ink-3 line-through decoration-ink-4')}>{task.name}</div>
              {normalizeStatus(task.status) === 'Blocked' ? (
                <div className="truncate text-[12px] font-semibold text-status-red-ink">
                  {task.blocked_reason ? `Blocked: ${task.blocked_reason}` : 'Blocked — no reason given yet'}
                </div>
              ) : null}
              <OriginChip task={task} className="mt-0.5" />
            </div>
          </div>
        );
      },
    },
    {
      key: 'project',
      header: 'Project',
      sortable: true,
      minWidth: 130,
      sortValue: (task) => taskProjects(task, projectNameById)[0]?.name || '',
      render: (task) => {
        const projects = taskProjects(task, projectNameById);
        if (!projects.length) return null;
        return (
          <span className="block max-w-[150px] truncate text-[13px] text-ink-2" title={projects.map((item) => item.name).join(', ')}>
            {projects[0].name}{projects.length > 1 ? ` +${projects.length - 1}` : ''}
          </span>
        );
      },
    },
    {
      key: 'assignees',
      header: 'Assignees',
      sortable: true,
      minWidth: 150,
      sortValue: (task) => taskAssignees(task)[0]?.name || '',
      render: (task) => {
        const people = taskAssignees(task);
        if (!people.length) return isClosed(task) ? null : <span className="text-[12.5px] font-semibold text-status-amber-ink">Unassigned</span>;
        return (
          <span className="flex min-w-0 items-center gap-1.5" title={people.map((person) => person.name).join(', ')}>
            <span className="flex shrink-0">
              {people.slice(0, 2).map((person, index) => (
                <PersonAvatar key={person.id} person={person} className={cn(index > 0 && '-ml-1.5 ring-2 ring-[color:var(--surface)]')} />
              ))}
            </span>
            <span className="max-w-[120px] truncate text-[13px] text-ink-2">
              {people[0].name}{people.length > 1 ? ` +${people.length - 1}` : ''}
            </span>
          </span>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      width: 96,
      sortValue: (task) => PRIORITY_RANK[priorityMeta(task.priority).label],
      render: (task) => <PriorityFlag priority={task.priority} className="text-[12.5px]" />,
    },
    {
      key: 'due',
      header: 'Due',
      sortable: true,
      minWidth: 120,
      sortValue: (task) => isoDate(task.due_date) || null,
      render: (task) => {
        if (isDone(task)) {
          return <span className="text-[12.5px] text-ink-3">{task.completed_at ? `Done ${formatDate(task.completed_at, { year: 'auto' })}` : 'Done'}</span>;
        }
        if (isCancelled(task)) return <span className="text-[12.5px] text-ink-3">Cancelled</span>;
        return <DueBadge task={task} className="text-[12px]" />;
      },
    },
    {
      key: 'duration',
      header: 'Duration / Progress',
      sortable: true,
      minWidth: 140,
      sortValue: (task) => durationInfo(task)?.progress ?? null,
      render: (task) => {
        const info = durationInfo(task);
        if (!info) return null;
        return (
          <div className="flex min-w-[130px] flex-col gap-1" title={`${info.total} working ${info.total === 1 ? 'day' : 'days'} · ${info.label}`}>
            <div className="flex items-center justify-between gap-2 whitespace-nowrap text-[12px]">
              <span className={cn('font-semibold text-ink-2', info.tone === 'red' && 'text-status-red-ink')}>{info.label}</span>
              <span className="text-ink-3">{info.total} d</span>
            </div>
            <ProgressBar value={info.progress} tone={info.tone === 'gray' ? 'gray' : info.tone} label={`${task.name} progress`} />
          </div>
        );
      },
    },
    {
      key: 'section',
      header: 'Section',
      defaultHidden: true,
      sortable: true,
      minWidth: 120,
      sortValue: sectionOf,
      render: (task) => <span className={cn('text-[13px] text-ink-2', sectionOf(task) === NO_SECTION && 'text-ink-3')}>{sectionOf(task)}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      defaultHidden: true,
      sortable: true,
      sortValue: (task) => (task.locations || [])[0] || '',
      render: (task) => ((task.locations || []).length ? <span className="text-[13px] text-ink-2">{task.locations.join(', ')}</span> : null),
    },
    {
      key: 'status',
      header: 'Status',
      defaultHidden: true, // the status icon in the Task cell already says it (with a word for screen readers)
      sortable: true,
      width: 112,
      sortValue: (task) => normalizeStatus(task.status),
      render: (task) => <StatusPill status={normalizeStatus(task.status)} domain="task" size="sm" />,
    },
    {
      key: 'created',
      header: 'Created',
      defaultHidden: true,
      sortable: true,
      defaultSortDir: 'desc',
      sortValue: (task) => task.created_at || null,
      render: (task) => (task.created_at ? <span className="text-[12.5px] text-ink-3">{formatDate(task.created_at)}{task.created_by ? ` · ${task.created_by}` : ''}</span> : null),
    },
    readOnly ? null : {
      key: 'actions',
      header: '',
      width: 52,
      hideable: false,
      render: (task) => (
        <RowActions
          label={`Actions for ${task.name}`}
          items={[
            { label: 'Open', icon: PanelRightOpen, onSelect: () => onOpen(task) },
            isClosed(task)
              ? { label: 'Reopen task', icon: RotateCcw, onSelect: () => onReopen(task) }
              : { label: 'Mark done', icon: CircleCheck, onSelect: () => onDone(task) },
            { label: 'Delete task…', icon: Trash2, danger: true, onSelect: () => onDelete(task) },
          ]}
        />
      ),
    },
  ].filter(Boolean), [onDelete, onDone, onOpen, onReopen, projectNameById, readOnly]);
}

const CLOSED_GROUPS = {
  done: { key: 'done', label: 'Done this week' },
  cancelled: { key: 'cancelled', label: 'Cancelled' },
};
const closedGroupOf = (task) => (isDone(task) ? CLOSED_GROUPS.done : CLOSED_GROUPS.cancelled);

/*
  A row sits in ONE group, so with Person / Project the table lists a task under its first person / project
  (the Board shows it in every lane). The group header says how many of its tasks are shared, and the page
  says how the table groups (see PendingTasksBoard).
*/
const groupByFor = (group, { projectNameById, sectionOrder }) => {
  if (group === 'person') {
    return (task) => {
      const person = taskAssignees(task)[0];
      return person ? { key: person.id, label: person.name } : { key: NONE_VALUE, label: 'Unassigned' };
    };
  }
  if (group === 'project') {
    return (task) => {
      const project = taskProjects(task, projectNameById)[0];
      return project ? { key: project.id, label: project.name } : { key: NONE_VALUE, label: 'No project' };
    };
  }
  if (group === 'due') {
    return (task) => {
      if (isClosed(task)) return closedGroupOf(task);
      const key = dueGroupOf(task);
      return { key, label: DUE_GROUPS.find((item) => item.key === key)?.label || key };
    };
  }
  return (task) => {
    const section = sectionOf(task);
    return { key: section, label: section, order: sectionOrder.get(section) };
  };
};

const LAST_GROUPS = [NONE_VALUE, 'done', 'cancelled'];
const sortGroupsFor = (group, sectionOrder) => (a, b) => {
  const lastA = LAST_GROUPS.indexOf(a.key);
  const lastB = LAST_GROUPS.indexOf(b.key);
  if (lastA >= 0 || lastB >= 0) return (lastA >= 0 ? lastA : -1) - (lastB >= 0 ? lastB : -1);
  if (group === 'due') return DUE_ORDER.indexOf(a.key) - DUE_ORDER.indexOf(b.key);
  if (group === 'section') {
    const orderA = sectionOrder.has(a.key) ? sectionOrder.get(a.key) : 9999;
    const orderB = sectionOrder.has(b.key) ? sectionOrder.get(b.key) : 9999;
    return orderA - orderB || String(a.label).localeCompare(String(b.label));
  }
  return String(a.label).localeCompare(String(b.label));
};

export default function TaskTable({
  tasks,
  columns,
  columnState,
  group = 'section',
  sections = [],
  projectNameById = {},
  selectedTaskId,
  sort,
  onSortChange,
  selectedKeys,
  onSelectionChange,
  members,
  projects,
  onOpen,
  onAdd,
  onBulkUpdate,
  onBulkDelete,
  empty,
  loading,
  pagingKey,
  readOnly = false,
}) {
  const sectionOrder = useMemo(() => new Map(sections.map((section, index) => [section, index])), [sections]);
  const groupBy = useMemo(() => groupByFor(group, { projectNameById, sectionOrder }), [group, projectNameById, sectionOrder]);
  const sortGroups = useMemo(() => sortGroupsFor(group, sectionOrder), [group, sectionOrder]);
  const pending = tasks.filter((task) => !isClosed(task));
  const overdue = pending.filter((task) => dueInfo(task).kind === 'overdue').length;
  const today = pending.filter((task) => dueInfo(task).kind === 'today').length;
  const doneCount = tasks.filter(isDone).length;
  const cancelledCount = tasks.filter(isCancelled).length;
  const renderGroupHeader = group === 'person' || group === 'project'
    ? (item) => {
      if (item.key === NONE_VALUE) return item.label;
      const shared = item.rows.filter((task) => (group === 'person' ? taskAssignees(task) : taskProjects(task, projectNameById)).length > 1).length;
      return (
        <>
          {item.label}
          {shared ? (
            <span className="ml-1.5 font-medium text-ink-3" data-testid="tasks-table-group-shared">
              · {shared} also with {group === 'person' ? 'other people' : 'other projects'}
            </span>
          ) : null}
        </>
      );
    }
    : undefined;

  return (
    <DataTable
      columns={columns}
      columnVisibility={columnState}
      rows={tasks}
      rowKey="id"
      sort={sort}
      onSortChange={onSortChange}
      selectable={!readOnly}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      getRowLabel={(task) => task.name}
      onRowOpen={onOpen}
      groupBy={groupBy}
      sortGroups={sortGroups}
      renderGroupHeader={renderGroupHeader}
      groupActions={group === 'section' && !readOnly && onAdd ? (item) => (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="shrink-0 text-ink-3"
          aria-label={`Add task to ${item.label}`}
          title={`Add task to ${item.label}`}
          onClick={() => onAdd({ section: item.key === NO_SECTION ? '' : item.key })}
        >
          <Plus aria-hidden="true" />
        </Button>
      ) : undefined}
      rowTestId={(task) => `task-row-${task.id}`}
      rowClassName={(task) => cn(task.id === selectedTaskId && 'bg-brand-soft hover:bg-brand-soft', isClosed(task) && 'opacity-75')}
      renderBulkBar={readOnly ? undefined : ({ selectedRows, clear, returnFocus }) => (
        <TaskBulkBar
          tasks={selectedRows}
          members={members}
          projects={projects}
          sections={sections}
          onClear={clear}
          returnFocus={returnFocus}
          onBulkUpdate={onBulkUpdate}
          onBulkDelete={(rows) => { clear(); return onBulkDelete(rows); }}
          testId="table-bulk-bar"
        />
      )}
      empty={empty || <EmptyState title="No tasks to show" titleAs="h2" />}
      loading={loading}
      pagingKey={pagingKey}
      ariaLabel="Tasks"
      testId="tasks-table"
      footer={(
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-b-card border-t border-line-2 px-4 py-2.5 text-[12.5px] text-ink-3" data-testid="tasks-table-footer">
          <span><b className="font-semibold text-ink-2">{formatNumber(pending.length, { maxDecimals: 0 })}</b> open {pending.length === 1 ? 'task' : 'tasks'}</span>
          <span aria-hidden="true">·</span>
          <span className={cn(overdue && 'font-semibold text-status-red-ink')}>{overdue} overdue</span>
          <span aria-hidden="true">·</span>
          <span className={cn(today && 'font-semibold text-status-amber-ink')}>{today} due today</span>
          {doneCount ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{doneCount} done this week</span>
            </>
          ) : null}
          {cancelledCount ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{cancelledCount} cancelled</span>
            </>
          ) : null}
        </div>
      )}
    />
  );
}
